/**
 * Thẻ BĐS trong danh sách admin — hiển thị tóm tắt và hành động.
 *
 * Hành động: xem info, sửa, xóa; duyệt nếu pending và có quyền.
 * Giá hiển thị đầy đủ (formatCurrency, không mask).
 */
import { ButtonSpinner } from '../Common/ButtonSpinner';
import {
  getTransactionLabel,
  PUBLICATION_STATUS_OPTIONS,
  PUBLICATION_STATUSES,
  PROPERTY_STATUS_OPTIONS,
} from '../../shared/constants/property';
import { TEXT } from '../../shared/languages';
import { formatCurrency } from '../../shared/utils/formatCurrency';

/** Ảnh placeholder khi BĐS chưa có ảnh */
const PLACEHOLDER_IMAGE =
  'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=480&q=80';

/** Lấy label từ mảng options theo value */
const getOptionLabel = (options, value) =>
  options.find((option) => option.value === value)?.label || value;

/** Format lượt xem theo locale vi-VN */
const formatViewCount = (viewCount) => new Intl.NumberFormat('vi-VN').format(viewCount || 0);

/**
 * Card BĐS trong trang quản lý admin.
 * @param {Object} props
 * @param {Object} props.property - Dữ liệu BĐS
 * @param {boolean} props.canApproveProperty - Có quyền duyệt BĐS pending
 * @param {boolean} props.canViewQuickInfo - Hiển thị nút xem info nhanh
 * @param {string|number|null} props.deletingPropertyId - ID đang xóa
 * @param {string|number|null} props.approvingPropertyId - ID đang duyệt
 */
export const AdminPropertyCard = ({
  property,
  canApproveProperty,
  canViewQuickInfo,
  deletingPropertyId,
  approvingPropertyId,
  onApprove,
  onDelete,
  onViewInfo,
  onEdit,
}) => {
  const common = TEXT.common;
  const coverImage = property?.images?.[0]?.url || PLACEHOLDER_IMAGE;

  return (
    <article className="admin-property-card">
      <div className="admin-property-card-media">
        <img src={coverImage} alt="" loading="lazy" />
        <span className="admin-property-card-type">
          {getTransactionLabel(property.transaction_type)}
        </span>
      </div>

      <div className="admin-property-card-body">
        <div className="admin-property-card-main">
          <div className="admin-property-card-copy">
            <h2>{property.title}</h2>
            <p>
              {[property.category?.name, property.location].filter(Boolean).join(' · ')}
            </p>
          </div>
          <strong className="admin-property-card-price">{formatCurrency(property.price)}</strong>
        </div>

        <div className="admin-property-card-meta">
          <span className={`status-badge ${property.status}`}>
            {getOptionLabel(PROPERTY_STATUS_OPTIONS, property.status)}
          </span>
          <span className={`status-badge ${property.publication_status}`}>
            {getOptionLabel(PUBLICATION_STATUS_OPTIONS, property.publication_status)}
          </span>
          <span className="admin-property-card-views">
            {formatViewCount(property.views)} {common.viewedBy}
          </span>
        </div>

        <div className="admin-property-card-footer">
          <div className="admin-property-card-actions">
            {canViewQuickInfo && (
              <button type="button" className="admin-property-action admin-property-action--ghost" onClick={() => onViewInfo(property)}>
                {common.info}
              </button>
            )}
            <button
              type="button"
              className="admin-property-action admin-property-action--ghost"
              onClick={() => onEdit(property)}
            >
              {common.edit}
            </button>
            <button
              type="button"
              className="admin-property-action admin-property-action--danger"
              disabled={deletingPropertyId === property.id}
              onClick={() => onDelete(property)}
            >
              {deletingPropertyId === property.id && <ButtonSpinner />}
              {deletingPropertyId === property.id ? common.deleting : common.delete}
            </button>
          </div>

          {canApproveProperty && property.publication_status === PUBLICATION_STATUSES.pending && (
            <button
              type="button"
              className="admin-property-action admin-property-action--success admin-property-card-approve"
              disabled={approvingPropertyId === property.id}
              onClick={() => onApprove(property)}
            >
              {approvingPropertyId === property.id && <ButtonSpinner />}
              {approvingPropertyId === property.id ? common.approving : common.approve}
            </button>
          )}
        </div>
      </div>
    </article>
  );
};
